import { useState, useEffect } from 'react';
import axios from 'axios';
import { formatSHPrice } from '../utils/priceFormatter';
import StakeSelector from './StakeSelector';
import { useStakeSelector } from '../hooks/useStakeSelector';
import { useBottomSheet } from '../contexts/BottomSheetContext';
import { toast } from './Toast.jsx';

const getApiUrl = () => {
  const RAILWAY_API = import.meta.env.VITE_API_URL || 'https://registration-app-production.up.railway.app';
  const LOCALHOST_API = 'http://localhost:3000';
  const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  return isLocalhost ? LOCALHOST_API : RAILWAY_API;
};

const TrustForm = ({ targetActor, onSuccess, context = 'profile', contextId = null }) => {
  const [loading, setLoading] = useState(false);
  const [trustReason, setTrustReason] = useState('');
  const [relatedProjects, setRelatedProjects] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState(context === 'project' && contextId ? String(contextId) : '');
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [contextProject, setContextProject] = useState(null);
  const { closeBottomSheet } = useBottomSheet();
  
  const {
    decayLots,
    lotStakes,
    totalStake,
    handleTotalStakeChange,
    handleLotStakeChange,
  } = useStakeSelector();

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log('🚀 信任処理開始');
    console.log('📊 totalStake:', totalStake);
    console.log('📦 lotStakes:', lotStakes);
    console.log('🎯 targetActor:', targetActor);
    setLoading(true);

    try {
      const amount = parseFloat(totalStake);
      console.log('💰 amount:', amount);
      if (isNaN(amount) || amount <= 0) {
        console.log('❌ 信任額が無効');
        toast.error('有効な信任額を入力してください');
        setLoading(false);
        return;
      }

      if (!decayLots || decayLots.length === 0) {
        console.log('❌ 使用可能なトークンがありません');
        toast.error('使用可能なトークンがありません');
        setLoading(false);
        return;
      }
      
      const sortedLots = [...decayLots].sort((a, b) => 
        new Date(a.decay_end_date) - new Date(b.decay_end_date)
      );
      const firstLot = sortedLots[0];
      console.log('🎲 firstLot (decay順):', firstLot);

      const targetActorId = targetActor.actorId || targetActor.actor_id || targetActor.id;
      console.log('👤 targetActorId:', targetActorId);
      
      const token = localStorage.getItem('token');
      console.log('🔑 token exists:', !!token);
      
      if (!lotStakes || Object.keys(lotStakes).length === 0) {
        console.log('❌ ステーク量が設定されていません');
        toast.error('信任量を入力してください');
        setLoading(false);
        return;
      }
      
      const selectedLotIds = Object.keys(lotStakes);
      console.log('🔍 lotStakes keys:', selectedLotIds);
      console.log('🔍 decayLots IDs:', decayLots.map(l => {
        const lotId = l.id || l.transaction_id;
        return {
          id: l.id,
          transaction_id: l.transaction_id,
          lotId: lotId,
          lotIdString: String(lotId),
          token_type: l.token_type,
          matchesStake: selectedLotIds.includes(String(lotId)) || selectedLotIds.includes(lotId)
        };
      }));
      
      const selectedLots = decayLots.filter(lot => {
        // useStakeSelectorはlot.transaction_idをキーとして使用しているので、それに合わせる
        const lotId = lot.transaction_id;
        // 数値IDと文字列IDの両方をチェック
        return selectedLotIds.includes(String(lotId)) || selectedLotIds.includes(lotId);
      });
      console.log('🔍 Selected lots:', selectedLots.map(l => ({ id: l.id, transaction_id: l.transaction_id, type: l.token_type, amount: l.current_amount, stakeAmount: lotStakes[l.transaction_id] })));
      
      if (selectedLots.length === 0) {
        console.log('❌ 選択されたロットが見つかりません');
        toast.error('選択されたトークンロットが無効です');
        setLoading(false);
        return;
      }
      
      const selectedTokenTypes = new Set(selectedLots.map(l => l.token_type));
      console.log('🔍 Token types in selected lots:', Array.from(selectedTokenTypes));
      
      // 複数トークンタイプ混在を許可（payment_token_typeは最初のロットのものを使用）
      const actualTokenType = selectedLots[0].token_type;
      console.log('🔍 Using token type:', actualTokenType);
      
      const requestBody = {
        trust_target_actor_id: targetActorId,
        trust_strength: 5,
        payment_token_type: actualTokenType,
        payment_amount: amount,
        stake_lots: lotStakes,
        total_stake: amount,
        trust_reason: trustReason || null,
        trust_context: context,
        context_id: contextId,
        related_project_id: selectedProjectId || null,
      };
      console.log('📤 Request body:', requestBody);
      
      const API_URL = getApiUrl();
      const response = await fetch(`${API_URL}/api/trust`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(requestBody),
      });

      console.log('📥 Response status:', response.status);
      const data = await response.json();
      console.log('📥 Response data:', data);

      if (response.ok && data.success) {
        console.log('✅ 信任成功');
        if (onSuccess) {
          await onSuccess(data.data);
        }
        closeBottomSheet();
      } else {
        console.log('⚠️ 信任失敗:', data.message);
        toast.error(data.message || '信任の実行に失敗しました');
      }
    } catch (err) {
      console.error('❌ 信任実行エラー:', err);
      toast.error('信任の実行中にエラーが発生しました');
    } finally {
      console.log('🏁 信任処理終了');
      setLoading(false);
    }
  };

  // Actorのdisplay_nameを表示（プロフィール画面で編集した名前）
  // displayName（キャメルケース）またはdisplay_name（スネークケース）を使用
  const displayName = targetActor?.displayName || targetActor?.display_name || '匿名ユーザー';
  const profileImagePath = targetActor?.profile_image_url || targetActor?.profileImageUrl || targetActor?.profile_image || targetActor?.iconUrl;
  const actorId = targetActor.actorId || targetActor.actor_id || targetActor.id;
  
  // 相対パスを絶対URLに変換
  const getAbsoluteImageUrl = (imagePath) => {
    if (!imagePath) return null;
    if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
      return imagePath;
    }
    if (imagePath.startsWith('/uploads/')) {
      return `${getApiUrl()}${imagePath}`;
    }
    return imagePath;
  };
  
  const profileImage = getAbsoluteImageUrl(profileImagePath);

  useEffect(() => {
    const fetchRelatedProjects = async () => {
      try {
        setLoadingProjects(true);
        let token = localStorage.getItem('token');
        
        if (!token) {
          console.error('❌ [TrustForm] No token found');
          setLoadingProjects(false);
          return;
        }
        
        // トークンのクリーンアップ（引用符、改行、スペースを削除）
        token = token.trim().replace(/^["']|["']$/g, '');
        
        const tokenParts = token.split('.');
        if (tokenParts.length !== 3) {
          console.error('❌ [TrustForm] Invalid token format. Parts:', tokenParts.length);
          console.error('Token preview:', token.substring(0, 50) + '...');
          setLoadingProjects(false);
          return;
        }
        
        const API_URL = getApiUrl();
        
        let decodedToken;
        try {
          // Base64-URL to Base64変換（JWTはBase64-URLエンコーディングを使用）
          let base64 = tokenParts[1].replace(/-/g, '+').replace(/_/g, '/');
          // パディング追加
          while (base64.length % 4) {
            base64 += '=';
          }
          decodedToken = JSON.parse(window.atob(base64));
        } catch (decodeError) {
          console.error('❌ [TrustForm] Token decode error:', decodeError);
          console.error('Token payload (first 30 chars):', tokenParts[1].substring(0, 30));
          setLoadingProjects(false);
          return;
        }
        
        const currentActorId = decodedToken.actorId || decodedToken.actor_id;
        
        console.log('🔍 [TrustForm] Fetching related projects for actor:', currentActorId);
        console.log('🔍 [TrustForm] Context:', context, 'ContextId:', contextId);
        
        // contextがprojectの場合、そのプロジェクト情報を取得
        if (context === 'project' && contextId) {
          const projectRes = await axios.get(`${API_URL}/api/projects/${contextId}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          const projectData = projectRes.data.data || projectRes.data;
          console.log('📦 [TrustForm] Context project:', projectData);
          setContextProject({
            project_id: projectData.project_id,
            title: projectData.title,
          });
        }
        
        const [ownedRes, memberRes] = await Promise.all([
          axios.get(`${API_URL}/api/project?owner_actor_id=${currentActorId}`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios.get(`${API_URL}/api/projects/actor/${currentActorId}/affiliations`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);
        
        console.log('📦 [TrustForm] Owned projects response:', ownedRes.data);
        console.log('📦 [TrustForm] Member projects response:', memberRes.data);
        
        const ownedProjectsData = ownedRes.data.project || ownedRes.data.data || ownedRes.data || [];
        const ownedProjects = (Array.isArray(ownedProjectsData) ? ownedProjectsData : []).map((p) => ({
          project_id: p.project_id,
          title: p.title,
          role: 'owner',
        }));
        
        const memberProjects = (memberRes.data.projects || []).map((p) => ({
          project_id: p.id,
          title: p.name,
          role: p.role,
        }));
        
        const allProjects = [...ownedProjects, ...memberProjects];
        console.log('✅ [TrustForm] Total projects found:', allProjects.length, allProjects);
        setRelatedProjects(allProjects);
      } catch (error) {
        console.error('❌ [TrustForm] 関連プロジェクトの取得に失敗:', error);
        console.error('❌ [TrustForm] Error details:', error.response?.data || error.message);
      } finally {
        setLoadingProjects(false);
      }
    };
    
    fetchRelatedProjects();
  }, [context, contextId]);

  return (
    <div className="space-y-4">
      <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
        <div className="flex items-center space-x-3">
          {profileImage ? (
            <img
              src={profileImage}
              alt={displayName}
              className="w-12 h-12 rounded-full object-cover"
            />
          ) : (
            <div className="w-12 h-12 rounded-full bg-gray-300 flex items-center justify-center text-xl">
              👤
            </div>
          )}
          <div>
            <p className="font-semibold text-gray-800">
              {displayName}
            </p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="mb-4">
          <StakeSelector
            decayLots={decayLots}
            totalStake={totalStake}
            lotStakes={lotStakes}
            onTotalChange={handleTotalStakeChange}
            onLotChange={handleLotStakeChange}
            helpText="減衰期が近いトークンから自動的に割り当てられます"
            showHelpText={true}
            hideTitle={false}
            customTitle="信任量（SH）"
          />
        </div>

        <div className="mb-4">
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            関連プロジェクト（任意）
          </label>
          {contextProject ? (
            <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-sm font-semibold text-green-800">
                📊 {contextProject.title}
              </p>
              <p className="text-xs text-green-600 mt-1">
                このプロジェクトの収支に記録されます
              </p>
            </div>
          ) : loadingProjects ? (
            <p className="text-sm text-gray-500">プロジェクト読み込み中...</p>
          ) : relatedProjects.length > 0 ? (
            <select
              value={selectedProjectId}
              onChange={(e) => setSelectedProjectId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
            >
              <option value="">選択しない</option>
              {relatedProjects.map((project) => (
                <option key={project.project_id} value={project.project_id}>
                  {project.title} ({project.role === 'owner' ? 'オーナー' : 'メンバー'})
                </option>
              ))}
            </select>
          ) : (
            <p className="text-sm text-gray-500">関連するプロジェクトがありません</p>
          )}
          {!contextProject && (
            <p className="text-xs text-gray-500 mt-1">
              この信任をプロジェクトの収支に記録する場合は選択してください
            </p>
          )}
        </div>

        <div className="mb-4">
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            信任理由（任意）
          </label>
          <textarea
            value={trustReason}
            onChange={(e) => setTrustReason(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none"
            rows="3"
            placeholder="例：この人の活動を応援したい、信頼できるストア運営者だから、など"
          />
        </div>

        <div className="mb-6 p-4 bg-gray-50 rounded-lg">
          <h3 className="font-semibold text-gray-700 mb-2">信任の効果</h3>
          <ul className="text-sm text-gray-600 space-y-1">
            <li>✅ トークンが減衰しません</li>
            <li>✅ 信任先の使途履歴が見えます</li>
            <li>✅ エコサイクルマイニングが発生します</li>
          </ul>
        </div>

        <div className="flex space-x-3">
          <button
            type="button"
            onClick={closeBottomSheet}
            className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            disabled={loading}
          >
            キャンセル
          </button>
          <button
            type="submit"
            className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400"
            disabled={loading}
          >
            {loading ? '実行中...' : '信任する'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default TrustForm;
